/**
 * `useExecution(workflowId)` — TanStack Query read hook for the latest run of
 * a single workflow.
 *
 * Implements the AC-1400 / AC-1377 P2.3 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4
 * (query key conventions), 5.2 (read-hook signatures), and 8.2 (Phase 2
 * verification table).
 *
 * ## Why this exists
 *
 * Today, `WorkflowEditor.tsx` reads the latest run via
 * `useWorkflowStore.loadExecution(workflowId)`, which performs a two-step
 * fetch — `apiService.getRunInstances(workflowId)` → pick the latest →
 * `apiService.getRunInstanceResults(latestRun.run_instance_id, true)` — and
 * writes a `WorkflowExecution` object into the store keyed by `workflowId`.
 *
 * That store-driven path tangles three concerns:
 *
 *   1. The one-shot fetch + cache (this hook owns it now).
 *   2. Polling lifecycle (start, stop, error backoff). **Stays in
 *      `useWorkflowStore` for now**, per TD section 5.2 and the user's
 *      explicit constraint on this ticket. Polling will migrate in a later
 *      AC-1377 phase if/when we move SSE invalidation into the hook layer.
 *   3. Derived UI state (`progress`, `currentStep`, `totalSteps`,
 *      `stepStatuses` map, etc.). Those derivations stay at the store / call
 *      site level — this hook returns the raw API snapshot only, so consumers
 *      can compute derived state from `data` without round-tripping through
 *      the store.
 *
 * Like `useWorkflow` and `useWorkflowSteps`, this hook MUST coexist with
 * `useWorkflowStore.loadExecution` during the Phase 2 transition. Both
 * paths can subscribe to the same execution concurrently — RQ's per-key
 * request dedup makes the duplicate fetch cheap. AC-1401 begins migrating
 * consumers; the legacy `loadExecution` function is not deleted in this
 * ticket.
 *
 * ## Behavior
 *
 * - The queryFn is a TWO-STEP fetch matching the pre-existing
 *   `loadExecution` flow:
 *
 *     1. `apiService.getRunInstances(workflowId)` — list all runs (sorted
 *        most-recent first by the backend).
 *     2. If `runInstances` is non-empty, pick `runInstances[0]` (the latest)
 *        and call `apiService.getRunInstanceResults(latestRun.run_instance_id,
 *        true)` to get results + session messages + session info map.
 *     3. If `runInstances` IS empty (no runs exist yet for this workflow —
 *        e.g. a freshly created workflow that has never been executed),
 *        return a NULL-RUN snapshot: `{ runInstance: null, runResults: [],
 *        sessionMessages: [], sessionInfoMap: {} }`. This is NOT an error
 *        condition. The data is well-defined (the workflow has never run);
 *        consumers render the "no execution yet" empty state.
 *
 * - The cache key is `queryKeys.workflowExecution(workflowId)` (resolves to
 *   `['workflow', Number(id), 'execution']`). It is hierarchical under
 *   `queryKeys.workflow(id)` so prefix invalidation cascades — a single
 *   `invalidateQueries({ queryKey: queryKeys.workflow(id) })` marks the
 *   workflow detail, its steps, AND its execution data all stale in one
 *   call. This is RQ's prefix-matching contract on array keys; do not
 *   flatten without reworking caller invalidation.
 *
 * - `enabled` is auto-disabled for empty IDs (`undefined`, `null`, `''`).
 *   Same rationale as the other read hooks — without this guard, a
 *   `useQuery` whose key includes `Number(undefined)` (`NaN`) would cache
 *   under a useless key AND fire two HTTP requests for an unknown ID.
 *   Today's call sites all guard with `if (!workflowId) return` inside
 *   `useEffect`; this hook bakes that guard in.
 *
 * - Caller-provided `options` flow through to `useQuery`. Callers can
 *   override `staleTime`, `enabled`, `refetchOnMount`, etc. per-call without
 *   forking the hook. The caller's `enabled` is AND'ed with the internal
 *   id-truthy guard, so passing `{ enabled: false }` always wins.
 *
 * - Caller MUST NOT override `queryKey` or `queryFn` — those are owned by
 *   the hook. The `Options` type below excludes them.
 *
 * ## Return shape
 *
 * The hook returns `UseQueryResult<WorkflowExecutionSnapshot>`. The snapshot
 * is the RAW API data — NOT the polling-aware `WorkflowExecution` shape used
 * by `useWorkflowStore`. Specifically, the snapshot intentionally omits the
 * polling state fields (`isPolling`, `pollingStartTime`, `lastPollTime`,
 * `pollErrorCount`, `lastUpdated`) and the derived UI fields (`progress`,
 * `currentStep`, `totalSteps`, `stepStatuses` Map, `stepProgress` Map,
 * `stepErrors` Map, `isRunning`, `isPaused`, `error`, etc.) — those belong
 * to either the polling layer or the consumer's render logic, not to the
 * cache.
 *
 * Consumers that today read `execution.runResults` from the store can read
 * `data?.runResults ?? []` from this hook with no semantic change.
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { RunInstance, RunResult, SessionInfo, SessionMessage } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Read-only snapshot of a workflow's latest execution as exposed by the
 * REST API. This shape is the cached value of the `useExecution` query —
 * see the JSDoc on the hook for what is intentionally excluded (polling
 * state, derived UI fields).
 */
export interface WorkflowExecutionSnapshot {
  /**
   * The most recent run instance for this workflow. `null` when the
   * workflow has never been executed (i.e. `getRunInstances` returned an
   * empty list). NOT an error condition.
   */
  runInstance: RunInstance | null;

  /** Results for the run identified by `runInstance`. Empty when no run. */
  runResults: RunResult[];

  /**
   * Session messages associated with the run, when available. The legacy
   * `loadExecution` flow tolerated a missing `session_messages` field by
   * defaulting to `[]`; this hook preserves that contract.
   */
  sessionMessages: SessionMessage[];

  /**
   * Session info keyed by session id. Same defaulting behavior as
   * `sessionMessages`.
   */
  sessionInfoMap: Record<number, SessionInfo>;
}

/**
 * Caller-overridable subset of `UseQueryOptions`. We intentionally exclude
 * `queryKey` and `queryFn` because they are owned by this hook — letting
 * callers override them defeats the centralization the migration depends on.
 */
export type UseExecutionOptions = Omit<
  UseQueryOptions<
    WorkflowExecutionSnapshot,
    Error,
    WorkflowExecutionSnapshot,
    ReturnType<typeof queryKeys.workflowExecution>
  >,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<WorkflowExecutionSnapshot>` from
 * TanStack Query.
 *
 * @param workflowId Workflow ID. May be `string`, `number`, `undefined`, or
 *                   `null`. Falsy values disable the query (no fetch fires).
 * @param options    Optional `useQuery` options. `queryKey` and `queryFn`
 *                   are excluded from this type because they are owned by
 *                   the hook.
 *
 * @example
 *   const { data, isLoading } = useExecution(workflowId);
 *   if (isLoading) return <Spinner />;
 *   if (!data?.runInstance) return <NoRunsYet />;
 *   return <RunResultsList results={data.runResults} />;
 */
export function useExecution(
  workflowId: string | number | undefined | null,
  options?: UseExecutionOptions,
): UseQueryResult<WorkflowExecutionSnapshot, Error> {
  // Treat `undefined`, `null`, and `''` as "no ID yet". Anything else is a
  // valid input — the queryKeys factory will coerce it to the canonical
  // numeric form. We pass the SAME canonical form to the API call below so
  // the cache key and the request URL agree.
  const hasId = workflowId !== undefined && workflowId !== null && workflowId !== '';

  // The caller's `enabled` (if provided) is AND'ed with our internal
  // truthy-id guard. Either being falsy keeps the query disabled.
  const callerEnabled = options?.enabled;
  const enabled = hasId && (callerEnabled === undefined ? true : callerEnabled);

  return useQuery<
    WorkflowExecutionSnapshot,
    Error,
    WorkflowExecutionSnapshot,
    ReturnType<typeof queryKeys.workflowExecution>
  >({
    // `as` cast: when `hasId` is false we still need to return SOME key
    // shape (RQ requires queryKey unconditionally), but `enabled: false`
    // guarantees the queryFn never runs and the placeholder key is never
    // populated. We use `0` as the placeholder ID so the cast is safe.
    queryKey: queryKeys.workflowExecution(hasId ? (workflowId as string | number) : 0),
    queryFn: async (): Promise<WorkflowExecutionSnapshot> => {
      // Step 1: list run instances. Backend returns most-recent first.
      // Forward `workflowId` as-is — the underlying URL interpolation
      // accepts either string or number; we cast for the current TS
      // signature (AC-1401's typing pass widens it directly).
      const runInstances = await apiService.getRunInstances(workflowId as string);

      const latestRun = runInstances[0];

      // No runs yet for this workflow. This is a well-defined "empty"
      // state, NOT an error — return a null-run snapshot so consumers can
      // render their empty UI without branching on `error`.
      if (!latestRun) {
        return {
          runInstance: null,
          runResults: [],
          sessionMessages: [],
          sessionInfoMap: {},
        };
      }

      // Step 2: fetch results + session for the latest run. The legacy
      // `loadExecution` flow defaults `session_messages` and
      // `session_info_map` when the API omits them; preserve that.
      const response = await apiService.getRunInstanceResults(
        latestRun.run_instance_id,
        true,
      );

      return {
        runInstance: latestRun,
        runResults: response.results,
        sessionMessages: response.session_messages ?? [],
        sessionInfoMap: response.session_info_map ?? {},
      };
    },
    ...options,
    enabled,
  });
}

export default useExecution;
